import { useMutation } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { useToast } from '@givebutter/primitives/toast';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import { UseFormTextArea } from 'components/form';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { GivebutterAxiosError } from 'types/error';

interface YearEndSummariesSettingsValues {
    memo: string;
}

const YearEndSummaries = () => {
    const { success, error } = useToast();
    const { settings, accountId } = useCurrentAccount();
    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();

    const EOYMemoDefault = settings.find(item => item.code === Settings.YearEndSummaryMemo.Code);

    const methods = Form.useGivebutterForm<YearEndSummariesSettingsValues>({
        defaultValues: {
            memo: EOYMemoDefault?.value || '',
        },
    });

    const {
        control,
        formState: { isDirty },
        path,
        handleSubmit,
    } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation<
        unknown,
        GivebutterAxiosError,
        YearEndSummariesSettingsValues
    >({
        mutationFn: ({ memo }) => {
            return updateAccount(accountId, {
                settings: [
                    {
                        code: Settings.YearEndSummaryMemo.Code,
                        value: memo || null,
                        active: !!memo,
                        name: 'eoy_summary_memo',
                        type: 'text',
                    },
                ],
            });
        },
        onSuccess: _response => {
            success('Year-End memo saved successfully!');
            invalidateUserData();
        },
        onError: e => {
            error(e.response?.data.message || 'Something went wrong. Please try again.');
        },
    });

    return (
        <Box className="flex flex-col gap-8 p-8">
            <div>
                <h3 className="text-2xl font-medium">Year-End Summary Memo</h3>
                <p>
                    This message will appear in the Year-End Summary PDFs. Use this space to provide
                    any relevant tax information or instructions to your donors.
                </p>
            </div>
            <Form.Provider {...methods}>
                <form
                    className="flex flex-col items-start gap-6"
                    onSubmit={handleSubmit(values => submit(values))}
                >
                    <Form.Container>
                        <Form.Label name={path('memo')} className="hidden">
                            Year-End Summary Memo
                        </Form.Label>

                        <UseFormTextArea name={path('memo')} control={control} rows={5} />
                    </Form.Container>

                    <Form.SubmitButton isSubmitting={isLoading} disabled={!isDirty}>
                        Save
                    </Form.SubmitButton>
                </form>
            </Form.Provider>
        </Box>
    );
};

export { YearEndSummaries };
